import { Injectable } from '@angular/core';
import {
    addDoc,
    collection,
    collectionData,
    CollectionReference,
    deleteDoc,
    doc,
    DocumentData,
    DocumentReference,
    Firestore,
    query,
    updateDoc,
    where,
} from '@angular/fire/firestore';
import { Functions, httpsCallable, HttpsCallableResult } from '@angular/fire/functions';
import { from, map, Observable, switchMap } from 'rxjs';
import { OrganizationData } from '../shared/models/organization-data.model';
import { UserRole } from '../shared/models/user.model';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root',
})
export class FirestoreService {
    private systemsCollection: CollectionReference<DocumentData>;

    constructor(
        private readonly firestore: Firestore,
        private functions: Functions,
        private authService: AuthService
    ) {
        this.systemsCollection = collection(this.firestore, 'organizations');
    }

    getOrganizations(): Observable<OrganizationData[]> {
        return this.authService.getAuthenticatedUserRole().pipe(
            switchMap((userRole) => {
                if (
                    userRole === UserRole.SUPER_ADMIN ||
                    userRole === UserRole.ADMIN ||
                    userRole === UserRole.DOMAIN_OPS
                ) {
                    const colRef = collection(this.firestore, 'organizations');
                    return collectionData(colRef, { idField: 'id' }).pipe(
                        map((orgs) => this.mapCreatedAt(orgs as OrganizationData[]))
                    );
                }

                return this.authService.getAuthenticatedUserUid().pipe(
                    switchMap((userId) => {
                        const colRef = collection(this.firestore, 'organizations');
                        const q = query(colRef, where('userId', '==', userId));
                        return collectionData(q, { idField: 'id' }).pipe(
                            map((orgs) => this.mapCreatedAt(orgs as OrganizationData[]))
                        );
                    })
                );
            })
        );
    }

    private mapCreatedAt(orgs: OrganizationData[]): OrganizationData[] {
        return orgs.map((org) => {
            const createdAt = org.createdAt;
            if (createdAt && 'seconds' in createdAt && typeof createdAt.seconds === 'number') {
                return {
                    ...org,
                    createdAt: new Date(createdAt.seconds * 1000),
                };
            }

            return org;
        });
    }

    createOrganization(systemData: OrganizationData): Observable<DocumentReference<DocumentData, DocumentData>> {
        return from(addDoc(this.systemsCollection, <OrganizationData>{ ...systemData }));
    }

    deleteOrganization(id: string): Observable<void> {
        const orgDocRef = doc(this.firestore, `organizations/${id}`);
        return from(deleteDoc(orgDocRef));
    }

    updateOrganization(id: string, data: Partial<OrganizationData>): Observable<void> {
        const docRef = doc(this.firestore, `organizations/${id}`);
        return from(updateDoc(docRef, data));
    }

    notifyReadyToShare(organizationId: string): Observable<HttpsCallableResult<unknown>> {
        const callable = httpsCallable(this.functions, 'notifyReadyToShare');
        return from(callable({ organizationId }));
    }
}
